import * as DialogPrimitive from '@radix-ui/react-dialog'
import { X } from 'lucide-react'
import { AnimatePresence, animate, motion, type PanInfo, useIsPresent, useMotionValue, useReducedMotion, useTransform } from 'motion/react'
import * as React from 'react'

import { usePressMotion } from '../lib/press-motion'
import { useIsMobile } from '../lib/useIsMobile'
import { cn } from '../lib/utils'

/**
 * A single modal surface with two presentations: a centered **dialog** on desktop and a
 * bottom **sheet** on mobile, chosen by breakpoint (< md → sheet). The sheet rises with a
 * spring and can be dismissed by dragging its handle down past ~28% of its height (or a
 * fast flick); the dialog pops in. Built on Radix Dialog (focus trap, portal, Escape,
 * scroll lock) + `motion` for the spring/drag. `prefers-reduced-motion` collapses all
 * animation to instant.
 *
 * It owns the chrome (backdrop, radius, shadow, close button, drag handle) and renders
 * `children` as the body — bring your own title (`ResponsiveDialogTitle`, required by
 * Radix for a11y unless you pass `aria-label`), content, and footer.
 *
 * Both surfaces cap their height against the viewport and scroll the body, so content is
 * free to grow without any of it ending up past an edge; the close button and the sheet's
 * drag handle stay pinned outside the scroller.
 */

type Presentation = 'auto' | 'dialog' | 'sheet'

/**
 * The app's stacking ladder, for reference:
 *
 * | layer | z | what lives there |
 * | --- | --- | --- |
 * | page chrome | < 50 | sticky headers, bottom nav |
 * | modal | 50 | every DS overlay — this dialog, Dialog, MobileSearchSheet, popovers, menus |
 * | over-modal | 70 | a confirmation raised FROM a custom full-screen surface that already took 50 |
 * | toasts | 100 | always on top, including over a dialog |
 *
 * `over-modal` leaves 60–69 for the surface doing the raising (the food-profile rule sheet sits at
 * 60/61) and stays clear of toasts.
 */
type Layer = 'modal' | 'over-modal'

const LAYER_Z: Record<Layer, string> = { modal: 'z-50', 'over-modal': 'z-[70]' }

export interface ResponsiveDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  children: React.ReactNode
  /** Force a presentation regardless of viewport. Default `'auto'` (dialog ≥ md, sheet below). */
  presentation?: Presentation
  /**
   * Which stacking layer to occupy. Default `'modal'` is the single layer every DS overlay shares.
   *
   * Pass `'over-modal'` when this dialog is raised from a **custom** full-screen surface that has
   * already claimed that layer — a discard confirmation opened from a bespoke sheet, say. Without it
   * the confirmation renders *behind* the very thing that raised it, which is invisible in review
   * because nothing in the DS nests this way. `className` can't fix it: that reaches the surface
   * card, not the positioning container or the backdrop, and both carry the z.
   */
  layer?: Layer
  /**
   * How the surface treats `children`.
   *
   * `padded` (default) puts them in a padded scroller — the right shape for a confirmation or a short
   * form, and what every existing caller gets.
   *
   * `raw` hands the surface over: no scroller, no padding, `children` become its direct flex-column
   * content. Use it when regions have to stay put while only the middle scrolls — a pinned title bar,
   * a pinned action bar. That can't be built on `padded`, because its single slot IS the scroller, so
   * anything in it scrolls away. With `raw` the caller owns which region scrolls and what each is
   * padded by; pair it with `sizing="fill"` for a surface that shouldn't resize as content changes.
   */
  layout?: 'padded' | 'raw'
  /**
   * `fit` (default) caps the height and lets content size the surface below that.
   *
   * `fill` drops the cap so a `h-*` in `className` governs unopposed, giving the surface **one height
   * in every state**. For an editor whose content changes while the user works: a surface that grows
   * as things are added and shrinks as they're removed moves its primary action out from under the
   * pointer mid-task. The trade is empty space when there's little content, which is the point.
   */
  sizing?: 'fit' | 'fill'
  /** Extra classes on the surface (dialog card / sheet) — e.g. a different `max-w-*`. */
  className?: string
  /** Hide the built-in close button. */
  hideClose?: boolean
  /**
   * Forwarded to Radix Content's `onOpenAutoFocus`. Pass `(e) => e.preventDefault()` to stop
   * the first field grabbing focus on open (avoids the mobile keyboard springing under a sheet).
   */
  onOpenAutoFocus?: (event: Event) => void
  /** Accessible name when the content renders no visible `ResponsiveDialogTitle`. */
  'aria-label'?: string
}

// Sheet rise + drag-settle spring — matches the design's .42s cubic-bezier(.32,.72,0,1) feel.
const SPRING = { type: 'spring' as const, stiffness: 380, damping: 34 }
// Dialog pop easing — the design's cubic-bezier(.2,.8,.3,1).
const POP_EASE = [0.2, 0.8, 0.3, 1] as const

export function ResponsiveDialog({
  open,
  onOpenChange,
  children,
  presentation = 'auto',
  layer = 'modal',
  layout = 'padded',
  sizing = 'fit',
  className,
  hideClose = false,
  onOpenAutoFocus,
  'aria-label': ariaLabel
}: ResponsiveDialogProps) {
  const autoMobile = useIsMobile()
  const isSheet = presentation === 'sheet' || (presentation === 'auto' && autoMobile)
  const reduced = !!useReducedMotion()
  const close = React.useCallback(() => onOpenChange(false), [onOpenChange])
  const layerZ = LAYER_Z[layer]
  // There's no Radix Trigger, so Radix has nowhere to return focus on close. Remember the opener ourselves.
  const returnFocusTo = React.useRef<HTMLElement | null>(null)

  return (
    <DialogPrimitive.Root open={open} onOpenChange={onOpenChange}>
      <AnimatePresence>
        {open && (
          <DialogPrimitive.Portal forceMount>
            {/* Backdrop — warm green-black scrim (token), click to close. */}
            <motion.div
              aria-hidden
              className={cn('fixed inset-0 bg-[var(--scrim)] backdrop-blur-[3px]', layerZ)}
              initial={reduced ? false : { opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: reduced ? 0 : 0.2, ease: 'easeOut' }}
            />
            <DialogPrimitive.Content
              forceMount
              aria-label={ariaLabel}
              aria-describedby={undefined}
              onOpenAutoFocus={(e) => {
                // Runs before focus moves in, so activeElement is still the opener.
                returnFocusTo.current = document.activeElement instanceof HTMLElement ? document.activeElement : null
                onOpenAutoFocus?.(e)
              }}
              onCloseAutoFocus={(e) => {
                e.preventDefault()
                returnFocusTo.current?.focus()
                returnFocusTo.current = null
              }}
              // We handle backdrop clicks on the flex container below; keep Radix from
              // closing on the same interaction (it would double-fire).
              onPointerDownOutside={(e) => e.preventDefault()}
              onInteractOutside={(e) => e.preventDefault()}
              className={cn('fixed inset-0 flex outline-none', layerZ, isSheet ? 'items-end justify-center' : 'items-center justify-center p-6')}
              onClick={(e) => {
                if (e.target === e.currentTarget) close()
              }}>
              {/* Radix requires a `Dialog.Title` DESCENDANT and warns in the console when there
                  isn't one — the `aria-label` above already supplies the accessible name, but Radix
                  looks for the node, not the name, so the warning fires anyway on every surface that
                  draws its own header. An unexplained console error is worse than noise: it teaches
                  people to scroll past warnings. So when a caller names the dialog with `aria-label`
                  instead of a visible title, mint the title Radix wants and hide it. `aria-label`
                  still wins for the accessible name, and it's the same string. */}
              {ariaLabel && <DialogPrimitive.Title className="sr-only">{ariaLabel}</DialogPrimitive.Title>}
              {isSheet ? (
                <SheetSurface reduced={reduced} hideClose={hideClose} onClose={close} layout={layout} sizing={sizing} className={className}>
                  {children}
                </SheetSurface>
              ) : (
                <DialogSurface reduced={reduced} hideClose={hideClose} onClose={close} layout={layout} sizing={sizing} className={className}>
                  {children}
                </DialogSurface>
              )}
            </DialogPrimitive.Content>
          </DialogPrimitive.Portal>
        )}
      </AnimatePresence>
    </DialogPrimitive.Root>
  )
}

interface SurfaceProps {
  reduced: boolean
  hideClose: boolean
  onClose: () => void
  layout: 'padded' | 'raw'
  sizing: 'fit' | 'fill'
  className?: string
  children: React.ReactNode
}

/**
 * `padded` wraps children in the scroller; `raw` hands them straight to the surface's flex column so
 * the caller can pin its own regions. Kept in one place so both presentations can't drift.
 */
function SurfaceBody({ layout, padding, children }: { layout: 'padded' | 'raw'; padding: string; children: React.ReactNode }) {
  if (layout === 'raw') return <>{children}</>
  return <div className={cn('min-h-0 flex-1 overflow-y-auto overscroll-contain', padding)}>{children}</div>
}

/* ─── Desktop: centered dialog ─────────────────────────────────────── */

function DialogSurface({ reduced, hideClose, onClose, layout, sizing, className, children }: SurfaceProps) {
  return (
    <motion.div
      className={cn(
        'pointer-events-auto relative flex w-full max-w-[456px] flex-col rounded-[24px] border border-border bg-card shadow-float',
        // `max-h-full` resolves against the p-6 container, so the card always stops an even
        // gutter short of the viewport and scrolls its body instead of spilling off both ends.
        // `fill` drops it so a caller's `h-*` isn't silently capped — twMerge treats `max-h` and
        // `h` as separate groups, so the cap would otherwise survive the override and win.
        sizing === 'fit' && 'max-h-full',
        className
      )}
      initial={reduced ? false : { opacity: 0, y: 12, scale: 0.985 }}
      animate={{ opacity: 1, y: 0, scale: 1 }}
      exit={reduced ? { opacity: 0 } : { opacity: 0, y: 8, scale: 0.99 }}
      transition={{ duration: reduced ? 0 : 0.3, ease: POP_EASE }}>
      {!hideClose && <CloseButton onClose={onClose} className="right-5 top-5" />}
      <SurfaceBody layout={layout} padding="px-8 pb-7 pt-8">
        {children}
      </SurfaceBody>
    </motion.div>
  )
}

/* ─── Mobile: bottom sheet with drag-to-dismiss ────────────────────── */

function SheetSurface({ reduced, hideClose, onClose, layout, sizing, className, children }: SurfaceProps) {
  const sheetRef = React.useRef<HTMLDivElement>(null)
  const y = useMotionValue(0)
  // The handle grows slightly as you pull down — a small tactile cue.
  const handleWidth = useTransform(y, [0, 96], [40, 56])

  // A drag past the threshold *requests* a dismissal — `onOpenChange(false)` — and the caller is free to
  // decline it. The food-profile rule sheet does exactly that: a drag inside a drill-in pops back to the
  // sheet's root, and a drag with unsaved work raises a confirmation. Either way the sheet stays open.
  //
  // So `y` can't be left where the finger stopped just because we asked. When the request is declined,
  // that leaves the sheet parked part-way off the bottom of the screen with no way back but dragging it
  // up by hand.
  //
  // It also can't be reset unconditionally, which is why this isn't a one-liner: when the dismissal IS
  // accepted, the exit animates `y: '100%'` from wherever the drag ended, and that continuity is the
  // whole reason the gesture feels attached to the sheet. Resetting first would snap it up, then down.
  //
  // `useIsPresent` separates the two — it goes false as soon as AnimatePresence starts this subtree's
  // exit, so the effect settles the sheet only when it's genuinely still here.
  const [dismissRequests, setDismissRequests] = React.useState(0)
  const isPresent = useIsPresent()

  const handleDragEnd = React.useCallback(
    (_: unknown, info: PanInfo) => {
      const height = sheetRef.current?.offsetHeight ?? 400
      if (info.offset.y > height * 0.28 || info.velocity.y > 500) {
        setDismissRequests((n) => n + 1)
        onClose()
      } else {
        animate(y, 0, SPRING)
      }
    },
    [onClose, y]
  )

  React.useEffect(() => {
    if (dismissRequests > 0 && isPresent) animate(y, 0, SPRING)
  }, [dismissRequests, isPresent, y])

  return (
    <motion.div
      ref={sheetRef}
      style={{ y }}
      className={cn(
        'pointer-events-auto relative flex w-full max-w-[520px] flex-col rounded-t-[26px] bg-card pb-6 shadow-sheet',
        // Cap the rise so a content-sized sheet can never outgrow the screen — an uncapped sheet is
        // bottom-anchored, so its overflow goes off the TOP edge with nothing to scroll and the first
        // rows become unreachable (#1141). 88dvh keeps a strip of scrim tappable, and `dvh` tracks
        // mobile browser chrome as it collapses.
        //
        // `fill` drops it, because a caller asking for one fixed height needs `dvh` OUT of the way:
        // it changes as chrome collapses, so the cap would resize the surface mid-interaction — the
        // exact thing a fixed height is chosen to prevent. Such a caller sets its own `h-*` in svh.
        sizing === 'fit' && 'max-h-[88dvh]',
        className
      )}
      initial={reduced ? false : { y: '100%' }}
      animate={{ y: 0 }}
      exit={reduced ? { opacity: 0 } : { y: '100%' }}
      transition={reduced ? { duration: 0 } : SPRING}>
      {/* Drag zone — the handle. Constrained to 0 with downward elastic; the shared `y`
          motion value moves the whole sheet as you drag. Drag-to-dismiss lives here and
          nowhere else, which is what keeps it from fighting the body's scroll gesture. */}
      <motion.div
        className="flex shrink-0 touch-none cursor-grab justify-center pb-1.5 pt-3 active:cursor-grabbing"
        drag="y"
        dragConstraints={{ top: 0, bottom: 0 }}
        dragElastic={{ top: 0, bottom: 0.6 }}
        onDrag={(_, info) => y.set(Math.max(0, info.offset.y))}
        onDragEnd={handleDragEnd}>
        <motion.div style={{ width: handleWidth }} className="h-[5px] rounded-full bg-gray-100 dark:bg-gray-700" />
      </motion.div>
      {!hideClose && <CloseButton onClose={onClose} className="right-[18px] top-3" />}
      <SurfaceBody layout={layout} padding="px-6 pb-1 pt-1">
        {children}
      </SurfaceBody>
    </motion.div>
  )
}

function CloseButton({ onClose, className }: { onClose: () => void; className?: string }) {
  // Sixth adopter of the shared beUI press — see lib/press-motion. The button is already round and
  // sits inset from the surface edge, so the focus ring is flush: an offset would only push it
  // toward the dialog's own corner radius without making it any easier to see.
  const press = usePressMotion()
  return (
    <motion.button
      type="button"
      {...press}
      onClick={onClose}
      aria-label="Close"
      className={cn(
        'absolute z-[3] flex h-[34px] w-[34px] items-center justify-center rounded-full text-gray-400 transition-colors hover:bg-muted hover:text-foreground',
        'focus-visible:outline-none focus-visible:ring-[3px] focus-visible:ring-pelorous-500 dark:focus-visible:ring-pelorous-300',
        className
      )}>
      <X className="h-5 w-5" strokeWidth={2.2} />
      <span className="sr-only">Close</span>
    </motion.button>
  )
}

/* ─── Title / Description (Radix a11y primitives; style to taste) ───── */

const ResponsiveDialogTitle = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Title>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Title>
>(({ className, ...props }, ref) => (
  <DialogPrimitive.Title ref={ref} className={cn('font-brand text-xl font-semibold leading-tight tracking-tight', className)} {...props} />
))
ResponsiveDialogTitle.displayName = 'ResponsiveDialogTitle'

const ResponsiveDialogDescription = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Description>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Description>
>(({ className, ...props }, ref) => (
  <DialogPrimitive.Description ref={ref} className={cn('text-sm text-muted-foreground', className)} {...props} />
))
ResponsiveDialogDescription.displayName = 'ResponsiveDialogDescription'

/* ─── Standard header — badge above a full-width title, on one left edge ───── */

/** Header badge tone: `brand` (green) for standard dialogs, `danger` (rose) for the
 *  destructive tier — a delete/end confirmation paired with a Keep/Cancel + destructive
 *  footer. See the Storybook DangerTier story. */
type HeaderTone = 'brand' | 'danger'

// The badge's soft tint + icon color, keyed by tone. Both light + dark are covered by the
// es-* tokens (rose-1 flips to a dark rose in dark mode).
const HEADER_BADGE_TONE: Record<HeaderTone, string> = {
  brand: 'bg-brand-green/10 text-brand-green',
  danger: 'bg-red-wash text-red'
}

export interface ResponsiveDialogHeaderProps {
  /** Badge glyph (~24px). Inherits the badge's tone via currentColor — size it yourself. */
  icon: React.ReactNode
  title: React.ReactNode
  description?: React.ReactNode
  /** Badge tone. Default `brand` (green); `danger` (rose) for destructive confirmations. */
  tone?: HeaderTone
  /** Extra classes on the header wrapper — typically the bottom spacing before the body. */
  className?: string
}

/**
 * The settled modal header: a soft-tinted badge stacked ABOVE a left-aligned, full-width
 * title, with an optional description below. Everything shares one left edge with the body
 * that follows. Give the wrapper its bottom spacing via `className` (e.g. `mb-5`). Pass
 * `tone="danger"` for the destructive tier (rose badge).
 */
function ResponsiveDialogHeader({ icon, title, description, tone = 'brand', className }: ResponsiveDialogHeaderProps) {
  return (
    <div className={className}>
      <div className={cn('mb-4 flex h-[54px] w-[54px] items-center justify-center rounded-[16px]', HEADER_BADGE_TONE[tone])}>{icon}</div>
      <ResponsiveDialogTitle className="text-2xl">{title}</ResponsiveDialogTitle>
      {description ? <ResponsiveDialogDescription className="mt-2 leading-relaxed">{description}</ResponsiveDialogDescription> : null}
    </div>
  )
}
ResponsiveDialogHeader.displayName = 'ResponsiveDialogHeader'

/* ─── Step header — a persistent counter for multi-step dialogs ───── */

export interface ResponsiveDialogStepHeaderProps {
  /** One-based index of the step on screen; fills that many segments. */
  step: number
  total: number
  /** The counter sentence, already localised — e.g. `Step 2 of 4`. */
  label: string
  className?: string
}

/**
 * An optional counter for a dialog that runs as a sequence: a monospaced label on the left, a
 * segmented track on the right. Render it as the FIRST child of the dialog body and keep it there
 * on every step — it answers "how much of this is left", which only works if it never moves.
 *
 * The track is decorative; the label carries the semantics, so screen readers hear the count once.
 */
function ResponsiveDialogStepHeader({ step, total, label, className }: ResponsiveDialogStepHeaderProps) {
  return (
    // pr-10 keeps the track clear of the surface's own close button.
    <div className={cn('mb-5 flex items-center justify-between gap-3.5 pr-10', className)}>
      <span className="whitespace-nowrap font-mono text-xs uppercase tracking-[0.08em] text-muted-foreground">{label}</span>
      <span aria-hidden className="flex gap-[5px]">
        {Array.from({ length: total }, (_, i) => (
          <i
            key={i}
            className={cn(
              'h-1 w-[22px] rounded-full transition-colors duration-[350ms] motion-reduce:transition-none',
              i < step ? 'bg-brand-green-700 dark:bg-brand-green-400' : 'bg-gray-200 dark:bg-gray-700'
            )}
          />
        ))}
      </span>
    </div>
  )
}
ResponsiveDialogStepHeader.displayName = 'ResponsiveDialogStepHeader'

export { ResponsiveDialogTitle, ResponsiveDialogDescription, ResponsiveDialogHeader, ResponsiveDialogStepHeader }
